import { useEffect, useState } from "react";
import { ukDate } from "@/lib/utils";
import { Plus, Trash2, Search, Loader2, CheckCircle2, Calculator, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Use of Funds & Disbursement Statement form (UK).
 *
 * Three deal modes via tabs:
 *   - topup        — settling an existing SF balance only
 *   - consolidation — settling third-party lenders only
 *   - both         — both at once
 */
export default function UseOfFundsForm({
  loanAmount = 0,
  arrangementFee = 0,
  breakdown,
  onChange,
  prefilledSummary = null,
  companyName = "",
}) {
  const lenders = breakdown?.lenders || [];
  const mode = breakdown?.mode || "topup";

  const existingBalance = parseFloat(breakdown?.existingSfBalance) || 0;
  const lenderTotal =
    mode === "topup"
      ? 0
      : lenders.reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0);
  const sfDeduction = mode === "consolidation" ? 0 : existingBalance;
  // Admin/arrangement fee is loaned alongside the principal and repaid
  // over the loan term — it is NOT deducted from the customer's disbursement.
  // So if the customer borrows £10k, they receive £10k (less only the
  // existing SF balance and any third-party lender settlements).
  const netAdvance = Math.max(
    0,
    (parseFloat(loanAmount) || 0) - sfDeduction - lenderTotal
  );

  const update = (patch) => onChange({ ...breakdown, ...patch });

  const updateLender = (idx, field, value) => {
    const next = [...lenders];
    next[idx] = { ...next[idx], [field]: value };
    update({ lenders: next });
  };

  const addLender = () => {
    if (lenders.length >= 10) return;
    update({ lenders: [...lenders, { name: "", amount: 0 }] });
  };

  const removeLender = (idx) => {
    const next = lenders.filter((_, i) => i !== idx);
    update({ lenders: next.length === 0 ? [{ name: "", amount: 0 }] : next });
  };

  const fmt = (n) =>
    `£${(parseFloat(n) || 0).toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <Card
      className="p-4 bg-amber-50 border-amber-200 space-y-4"
      data-testid="use-of-funds-form"
    >
      <div>
        <h4 className="text-sm font-semibold text-amber-900">
          Use of Funds & Disbursement Statement
        </h4>
        <p className="text-xs text-amber-800 mt-0.5">
          Customer signs this acknowledging how the loan will be disbursed.
          Pick the deal type below.
        </p>
        {prefilledSummary?.source === "application" && (
          <p
            className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-2 inline-block"
            data-testid="uof-prefill-hint"
          >
            Auto-filled from the credit team's net-in-hand confirmation
            {prefilledSummary.hasExistingSf ? " · existing SF#" : ""}
            {prefilledSummary.hasExistingBalance ? " · outstanding balance" : ""}
            {prefilledSummary.lenderCount > 0
              ? ` · ${prefilledSummary.lenderCount} lender${
                  prefilledSummary.lenderCount > 1 ? "s" : ""
                }`
              : ""}
            . Edit any value before generating.
          </p>
        )}
      </div>

      <Tabs value={mode} onValueChange={(v) => update({ mode: v })}>
        <TabsList className="grid grid-cols-3 w-full" data-testid="uof-mode-tabs">
          <TabsTrigger value="topup" data-testid="uof-tab-topup">
            Top-up / Refinance
          </TabsTrigger>
          <TabsTrigger value="consolidation" data-testid="uof-tab-consolidation">
            Loan Consolidation
          </TabsTrigger>
          <TabsTrigger value="both" data-testid="uof-tab-both">
            Both
          </TabsTrigger>
        </TabsList>

        {(mode === "topup" || mode === "both") && (
          <TabsContent value={mode} forceMount className="pt-3 space-y-3">
            <TopUpPanel
              breakdown={breakdown}
              update={update}
              companyName={companyName}
            />
          </TabsContent>
        )}

        {(mode === "consolidation" || mode === "both") && (
          <TabsContent value={mode} forceMount className="pt-3 space-y-3">
            <ConsolidationPanel
              lenders={lenders}
              updateLender={updateLender}
              addLender={addLender}
              removeLender={removeLender}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Disbursement summary */}
      <div className="bg-white rounded-md border border-amber-200 p-3">
        <h5 className="text-xs font-semibold text-slate-700 mb-2">
          Disbursement breakdown
        </h5>
        <dl className="text-xs space-y-1">
          <Row label="Loan amount" value={fmt(loanAmount)} />
          {sfDeduction > 0 && (
            <Row
              label={`Less: Existing SF balance (${
                breakdown?.existingSfNumber || "—"
              })`}
              value={`(${fmt(sfDeduction)})`}
            />
          )}
          {mode !== "topup" &&
            lenders
              .filter((l) => l.name && l.amount > 0)
              .map((l, i) => (
                <Row
                  key={i}
                  label={`Less: Settlement to ${l.name}${l.reference ? ` (ref ${l.reference})` : ""}`}
                  value={`(${fmt(l.amount)})`}
                />
              ))}
          <div className="border-t pt-1 mt-1">
            <Row
              label="Net amount payable to borrower"
              value={fmt(netAdvance)}
              bold
            />
          </div>
        </dl>
        {(parseFloat(arrangementFee) || 0) > 0 && (
          <p className="text-[10px] text-slate-500 pt-1">
            Arrangement fee {fmt(arrangementFee)} is included in the principal and repaid over the loan term — it isn't deducted from the disbursement.
          </p>
        )}
      </div>
    </Card>
  );
}

// ----------------------------------------------------------------------
// Top-up panel — mirrors the Credit Approved Deals "Confirm Net in Hand"
// flow: fuzzy LoanDisk borrower search + manual SF# search, plus Airtable
// today's payment lookup (LoanDisk is 24h behind).
// ----------------------------------------------------------------------
function TopUpPanel({ breakdown, update, companyName }) {
  const [loans, setLoans] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sfSearch, setSfSearch] = useState("");
  const [sfSearching, setSfSearching] = useState(false);
  const [autoSearched, setAutoSearched] = useState(false);
  const [airtable, setAirtable] = useState({
    found: false,
    total_amount: 0,
    payments: [],
  });
  const [airtableLoading, setAirtableLoading] = useState(false);
  const [airtableRefreshedAt, setAirtableRefreshedAt] = useState(null);
  const [loandiskError, setLoandiskError] = useState(null);
  const [restructuredExcluded, setRestructuredExcluded] = useState(0);

  const cleanQuery = (companyName || "")
    .replace(/^SF-\d+\s*-?\s*/i, "")
    .replace(/\s*\(\s*TU\s*\)\s*/i, "")
    .replace(/\s+(Ltd|Limited|LTD|LIMITED)\.?$/i, "")
    .trim();

  // Auto-search by company name once when the company is known
  useEffect(() => {
    if (!cleanQuery || autoSearched) return;
    searchByName(cleanQuery, { silent: true });
    setAutoSearched(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanQuery]);

  const searchByName = async (query, { silent = false } = {}) => {
    if (!query) return;
    try {
      setSearching(true);
      const { data } = await api.get("/loandisk/search-by-borrower", {
        params: { borrower_name: query },
      });
      setLoans(data?.loans || []);
      setRestructuredExcluded(data?.restructured_excluded || 0);
      // Surface LoanDisk auth / API errors so the operator doesn't think
      // it's just a "no matches" situation
      if (data?.status === "auth_error" || data?.api_error) {
        if (!silent) {
          toast.error(
            `LoanDisk: ${data.api_error || "API error"} — check credentials with admin`
          );
        }
        setLoandiskError(data.api_error || "LoanDisk API error");
      } else {
        setLoandiskError(null);
        if (!silent) {
          if (data?.loans?.length) {
            toast.success(`Found ${data.loans.length} live loan(s) in LoanDisk`);
          } else {
            toast.info(
              `No live loans found (scanned ${data?.borrowers_scanned || 0} borrowers)`
            );
          }
        }
      }
    } catch (err) {
      toast.error("LoanDisk search failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSearching(false);
    }
  };

  const searchBySf = async () => {
    if (!sfSearch.trim()) return;
    try {
      setSfSearching(true);
      const { data } = await api.get(`/loandisk/get-loan/${sfSearch.trim()}`);
      if (data?.found && data.loan) {
        setLoans([data.loan]);
        if (data.loan.is_restructured) {
          toast.error(
            data.warning ||
              "This loan was RESTRUCTURED — its balance is rolled into the replacement loan and can't be used as a top-up balance.",
            { duration: 9000 }
          );
        } else {
          await selectLoan(data.loan);
          toast.success("Existing loan found");
        }
      } else {
        setLoans([]);
        toast.info("No loan found with that SF number");
      }
    } catch (err) {
      toast.error("LoanDisk lookup failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSfSearching(false);
    }
  };

  const fetchAirtablePayments = async (sfNum, borrowerName, balance, loanId) => {
    if (!sfNum) return;
    try {
      setAirtableLoading(true);
      const { data } = await api.get("/contracts/loandisk/todays-airtable-payments", {
        params: { sf_number: sfNum, company_name: borrowerName },
      });
      const todaysAmount = parseFloat(data?.total_amount || 0);
      setAirtable({
        found: !!data?.found,
        total_amount: todaysAmount,
        payments: data?.payments || [],
      });
      setAirtableRefreshedAt(new Date());
      const adjusted = Math.max(0, balance - todaysAmount);
      update({
        existingSfNumber: sfNum,
        existingSfBalance: adjusted,
        selectedLoandiskLoanId: loanId,
        selectedLoandiskBorrower: borrowerName,
      });
      if (data?.found && todaysAmount > 0) {
        toast.info(
          `£${todaysAmount.toLocaleString()} paid today (Airtable) — balance adjusted`
        );
      }
    } catch (err) {
      console.warn("Airtable lookup failed:", err);
      setAirtable({ found: false, total_amount: 0, payments: [] });
    } finally {
      setAirtableLoading(false);
    }
  };

  const refreshAirtable = async () => {
    const sfNum = breakdown?.existingSfNumber;
    const borrowerName = breakdown?.selectedLoandiskBorrower || companyName;
    const loanId = breakdown?.selectedLoandiskLoanId;
    // Use the raw LoanDisk balance (un-adjusted) so we can recompute fresh
    const selectedLoan = loans.find((l) => l.loan_id === loanId);
    const rawBalance = selectedLoan
      ? parseFloat(
          selectedLoan.balance_amount ||
            selectedLoan.outstanding_balance_parsed ||
            selectedLoan.principal_balance_amount ||
            0
        )
      : parseFloat(breakdown?.existingSfBalance || 0) + parseFloat(airtable.total_amount || 0);
    await fetchAirtablePayments(sfNum, borrowerName, rawBalance, loanId);
    toast.success("Airtable payments refreshed");
  };

  const selectLoan = async (loan) => {
    if (loan.is_restructured) {
      toast.error(
        "This loan was RESTRUCTURED — its balance is rolled into the replacement loan and can't be used as a top-up balance.",
        { duration: 9000 }
      );
      return;
    }
    const balance = parseFloat(
      loan.balance_amount ||
        loan.outstanding_balance_parsed ||
        loan.principal_balance_amount ||
        loan.outstanding_balance ||
        0
    );
    const sfNum =
      loan.loan_application_id ||
      ((loan.borrower_business_name || loan.borrower_name || "")
        .match(/SF-?(\d+)/i)?.[0] || "");
    const borrowerName = loan.borrower_business_name || loan.borrower_name || "";

    // Persist selection up to the wizard
    update({
      existingSfNumber: sfNum,
      existingSfBalance: balance,
      selectedLoandiskLoanId: loan.loan_id,
      selectedLoandiskBorrower: borrowerName,
    });

    if (sfNum) {
      await fetchAirtablePayments(sfNum, borrowerName, balance, loan.loan_id);
    }
  };

  const selectedId = breakdown?.selectedLoandiskLoanId;
  const rawBalance =
    (loans.find((l) => l.loan_id === selectedId)?.balance_amount) ||
    (loans.find((l) => l.loan_id === selectedId)?.outstanding_balance_parsed) ||
    0;
  const balanceFloat = parseFloat(rawBalance);
  const todayPaid = parseFloat(airtable?.total_amount || 0);
  const adjustedBalance = Math.max(0, balanceFloat - todayPaid);

  return (
    <div className="space-y-3" data-testid="topup-panel">
      <div className="bg-white rounded-md border border-amber-200 p-3 space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <Search className="w-3.5 h-3.5" />
          Live LoanDisk match
          {cleanQuery && (
            <span className="font-normal text-slate-500">· "{cleanQuery}"</span>
          )}
        </div>

        {/* Search by SF# */}
        <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
          <Input
            placeholder="Enter existing SF# (e.g. SF-14447)"
            value={sfSearch}
            onChange={(e) => setSfSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchBySf()}
            className="bg-white h-9"
            data-testid="topup-sf-search-input"
          />
          <Button
            type="button"
            size="sm"
            onClick={searchBySf}
            disabled={sfSearching || !sfSearch.trim()}
            className="h-9"
            data-testid="topup-sf-search-btn"
          >
            {sfSearching && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            <Search className="w-3.5 h-3.5 mr-1" /> SF#
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => searchByName(cleanQuery)}
            disabled={searching || !cleanQuery}
            className="h-9"
            data-testid="topup-name-search-btn"
            title={cleanQuery ? `Search LoanDisk for ${cleanQuery}` : "Pick a company first"}
          >
            {searching && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            By name
          </Button>
        </div>

        {/* Results list */}
        {loans.length > 0 && (
          <div className="space-y-1.5" data-testid="loandisk-results">
            {loans.map((loan) => {
              const balance = parseFloat(
                loan.balance_amount ||
                  loan.outstanding_balance_parsed ||
                  loan.principal_balance_amount ||
                  0
              );
              const isSelected = breakdown?.selectedLoandiskLoanId === loan.loan_id;
              return (
                <button
                  key={loan.loan_id}
                  type="button"
                  onClick={() => selectLoan(loan)}
                  className={`w-full text-left p-2 rounded border text-xs transition-colors ${
                    isSelected
                      ? "bg-emerald-50 border-emerald-300"
                      : "bg-white border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`loandisk-loan-${loan.loan_id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {isSelected && (
                          <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                        )}
                        <span className="font-medium truncate">
                          {loan.borrower_business_name || loan.borrower_name || "—"}
                        </span>
                      </div>
                      <div className="text-slate-500 mt-0.5 flex items-center gap-2">
                        <span className="font-mono">
                          {loan.loan_application_id ? `SF# ${loan.loan_application_id}` : `Loan #${loan.loan_id}`}
                        </span>
                        {loan.loan_disbursed_date && (
                          <span>· Disbursed {ukDate(loan.loan_disbursed_date)}</span>
                        )}
                      </div>
                      {loan.is_restructured && (
                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300" data-testid="topup-restructured-badge">
                          RESTRUCTURED — balance rolled into replacement loan
                        </span>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`font-semibold ${loan.is_restructured ? "text-amber-700 line-through" : "text-slate-900"}`}>
                        £{balance.toLocaleString("en-GB", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                      <div className="text-[10px] text-slate-500">Outstanding</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {restructuredExcluded > 0 && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="topup-restructured-note">
            {restructuredExcluded} restructured loan(s) hidden — that balance is already rolled into the replacement loan.
          </div>
        )}

        {/* Airtable today's payment summary */}
        {selectedId && (
          <div
            className="rounded border p-2 text-xs bg-slate-50 border-slate-200 space-y-1"
            data-testid="airtable-payments-summary"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-semibold text-slate-700">
                <Calculator className="w-3 h-3" />
                Settlement amount (Airtable adjusted)
                {airtableLoading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
              </div>
              <button
                type="button"
                onClick={refreshAirtable}
                disabled={airtableLoading}
                className="text-[11px] text-blue-700 hover:underline flex items-center gap-1 disabled:opacity-50"
                data-testid="airtable-refresh-btn"
                title="Re-pull today's Airtable payments — useful if the wizard has been sitting open or a payment posted while you were working"
              >
                <RefreshCw className={`w-3 h-3 ${airtableLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">LoanDisk balance</span>
              <span className="font-mono">£{balanceFloat.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between text-emerald-700">
              <span>Less: Today's Airtable payment {airtable.found ? "✓" : "(none today)"}</span>
              <span className="font-mono">−£{todayPaid.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            {airtable.found && airtable.payments?.length > 0 && (
              <div className="pl-3 space-y-0.5 text-[11px] text-emerald-700">
                {airtable.payments.map((p, i) => (
                  <div key={i} className="flex justify-between">
                    <span className="truncate">{p.description || `Payment ${i + 1}`}</span>
                    <span className="font-mono">£{parseFloat(p.amount || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between font-semibold text-slate-900 border-t pt-1 mt-1">
              <span>Existing SF balance to settle</span>
              <span className="font-mono">£{adjustedBalance.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <p className="text-[10px] text-slate-500">
              LoanDisk posts payments 24h behind, so today's payment is pulled live from Airtable and deducted.
              {airtableRefreshedAt && (
                <span className="ml-1">
                  Last refreshed {airtableRefreshedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}.
                </span>
              )}
            </p>
          </div>
        )}

        {!searching && !sfSearching && loans.length === 0 && autoSearched && !loandiskError && (
          <p className="text-xs text-slate-500">
            No live loans found in LoanDisk for "{cleanQuery}". Use SF# search or enter the existing SF# manually below.
          </p>
        )}

        {loandiskError && (
          <div className="text-xs rounded border border-red-300 bg-red-50 p-2 text-red-800" data-testid="loandisk-error">
            <span className="font-semibold">LoanDisk error:</span> {loandiskError}
            <p className="text-[11px] mt-1 text-red-700">
              Search returned no results because LoanDisk rejected the call. Check the LoanDisk API key in Admin Settings (or ask an admin to refresh credentials). Enter the existing SF# manually below to continue.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Existing SimplyFunded agreement (SFNO)</Label>
          <Input
            placeholder="e.g. SF-1234"
            value={breakdown?.existingSfNumber || ""}
            onChange={(e) => update({ existingSfNumber: e.target.value })}
            className="bg-white h-9"
            data-testid="uof-existing-sfno"
          />
        </div>
        <div>
          <Label className="text-xs">Existing SF balance to settle (£)</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={breakdown?.existingSfBalance || 0}
            onChange={(e) => update({ existingSfBalance: e.target.value })}
            className="bg-white h-9"
            data-testid="uof-existing-balance"
          />
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Consolidation panel — third-party lenders only
// ----------------------------------------------------------------------
function ConsolidationPanel({ lenders, updateLender, addLender, removeLender }) {
  const [catalog, setCatalog] = useState([]);

  // Pre-populated lender catalog from /api/contracts/third-party-lenders
  // (managed in Portal Settings → Lenders). Acts as autocomplete so the
  // operator always picks consistent names — no "iwoca" vs "IWOCA" drift
  // in customer-facing docs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/contracts/third-party-lenders");
        if (!cancelled) setCatalog(data?.lenders || []);
      } catch (err) {
        console.warn("Could not load lender catalog:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs">
          Third-party lenders to settle directly (up to 10)
        </Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addLender}
          disabled={lenders.length >= 10}
          className="h-7 text-xs"
          data-testid="uof-add-lender"
        >
          <Plus className="w-3 h-3 mr-1" /> Add lender
        </Button>
      </div>

      <datalist id="uof-lender-catalog">
        {catalog.map((l) => (
          <option key={l.id} value={l.name} />
        ))}
      </datalist>

      {/* Column headers */}
      <div className="grid grid-cols-12 gap-2 mb-1 text-[10px] uppercase tracking-wide text-slate-500 font-semibold px-1">
        <div className="col-span-5">Lender</div>
        <div className="col-span-3">Settlement £</div>
        <div className="col-span-3">Their loan reference</div>
        <div className="col-span-1"></div>
      </div>

      <div className="space-y-2">
        {lenders.map((lender, idx) => (
          <div
            key={idx}
            className="grid grid-cols-12 gap-2"
            data-testid={`uof-lender-row-${idx}`}
          >
            <div className="col-span-5">
              <Input
                list="uof-lender-catalog"
                placeholder={`Pick or type lender ${idx + 1}`}
                value={lender.name || ""}
                onChange={(e) => updateLender(idx, "name", e.target.value)}
                className="bg-white h-9"
                data-testid={`uof-lender-name-${idx}`}
              />
            </div>
            <div className="col-span-3">
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={lender.amount || 0}
                onChange={(e) => updateLender(idx, "amount", e.target.value)}
                className="bg-white h-9"
                data-testid={`uof-lender-amount-${idx}`}
              />
            </div>
            <div className="col-span-3">
              <Input
                placeholder="Loan/payment reference"
                value={lender.reference || ""}
                onChange={(e) => updateLender(idx, "reference", e.target.value)}
                className="bg-white h-9"
                data-testid={`uof-lender-reference-${idx}`}
              />
            </div>
            <div className="col-span-1 flex justify-center">
              <button
                type="button"
                onClick={() => removeLender(idx)}
                className="text-slate-400 hover:text-red-600"
                data-testid={`uof-remove-lender-${idx}`}
                title="Remove lender"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-slate-500 mt-2">
        Lender names come from the catalog managed in Portal Settings → Lenders. The reference is the lender's own loan/payment reference number we put on the bank transfer.
      </p>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <dt className={bold ? "font-semibold text-slate-900" : "text-slate-700"}>
        {label}
      </dt>
      <dd
        className={
          bold ? "font-semibold text-slate-900" : "text-slate-700 font-mono"
        }
      >
        {value}
      </dd>
    </div>
  );
}
